import { SubTask } from 'interfaces';
import {
  RemainingSecondsParams,
  ToDoPlayerSubTask
} from 'interfaces/todos.interface';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { DEFAULT_TO_DO_PLAYER_TASK } from 'assets/data';
import { PlayerDataType, ToDoPlayerTask } from 'interfaces';
import type { ToDoTask } from 'interfaces/common.interface';
import { capturePostHogEvent } from 'services/posthog.service';
import { POST_HOG_EVENT } from 'constants/events';
import type { AppDispatch } from 'store';
import {
  updatePlayerIsReadyToSendUpdates,
  updatePlayerTasks,
  updateTasks
} from 'store/reducer/to-do/slice';
import {
  formatToDoPlayerTaskTimeConstraints,
  timeDurationPartsToSeconds
} from 'utils/helpers';
import { formatTasksForPlayer } from 'utils/todo.util';
import { TO_DO_PLAYER } from 'constants/general';
import { RANGE_SLIDER_STEPS } from 'constants/topPrioritization';
import {
  clamp,
  formatToFixedDecimals,
  ratioToPercent
} from 'utils/number.util';
import { cn } from 'utils/cn';

/** Drops nullish or id-less entries that would crash task list renders. */
export const filterValidPlayerTasks = (
  tasks: ToDoPlayerTask[] | undefined | null
): ToDoPlayerTask[] =>
  (tasks ?? []).filter((task): task is ToDoPlayerTask => Boolean(task?.id));

/** Strip client-only player fields before persisting a task to the API. */
export const stripTodoPlayerTaskForPersist = (
  task: ToDoPlayerTask
): ToDoTask => {
  const {
    focusedDuration: _focusedDuration,
    startTime: _startTime,
    endTime: _endTime,
    isPlaying: _isPlaying,
    isPaused: _isPaused,
    ...rest
  } = task;
  return rest;
};

export const EMPTY_DURATION_DISPLAY = '--:--:--';

const sanitizeDurationDigits = (raw: string) => raw.replace(/[^0-9]/g, '');

/** Whole hours and minutes (no seconds) for the player duration editor. */
export const splitPlayerDurationSeconds = (totalSeconds?: number) => {
  const total = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return { hours, minutes };
};

export const parsePlayerHoursMinutesDurationInput = (
  hoursRaw: string,
  minutesRaw: string
): {
  sanitizedHours: string;
  sanitizedMinutes: string;
  totalSeconds: number | null;
} => {
  const sanitizedHours = sanitizeDurationDigits(hoursRaw);
  const sanitizedMinutes = sanitizeDurationDigits(minutesRaw);
  const hoursNum = parseInt(sanitizedHours || '0', 10);
  const minutesNum = parseInt(sanitizedMinutes || '0', 10);
  const total = timeDurationPartsToSeconds({
    hours: hoursNum,
    minutes: minutesNum,
    seconds: 0
  });
  if (isNaN(total) || total <= 0) {
    return { sanitizedHours, sanitizedMinutes, totalSeconds: null };
  }
  return { sanitizedHours, sanitizedMinutes, totalSeconds: total };
};

export const formatDuration = (durationInSeconds?: number | null) => {
  if (durationInSeconds === undefined || durationInSeconds === null) {
    return EMPTY_DURATION_DISPLAY;
  }

  return moment()
    .startOf('date')
    .add(Math.max(durationInSeconds, 0), 'seconds')
    .format('HH[h]:mm[m]:ss[s]');
};

export const formatAlignmentScore = (score?: number) => {
  if (score === undefined || score === null || Number.isNaN(score)) {
    return '--';
  }

  return `${score.toFixed(2)}%`;
};

export const getSelectedTaskRawProgress = (
  totalDuration: number,
  focusedDuration?: number,
  counter?: number
) => {
  const elapsedFromTask = focusedDuration ?? 0;
  const elapsedFromCounter = totalDuration - (counter ?? totalDuration);
  const elapsedDuration = Math.min(
    totalDuration,
    Math.max(0, Math.max(elapsedFromTask, elapsedFromCounter))
  );
  return totalDuration > 0 ? elapsedDuration / totalDuration : 0;
};

export const mapToDoPlayerSubTaskToSubTask = (
  subtask: ToDoPlayerSubTask
): SubTask => ({
  name: subtask.name,
  is_completed: subtask.is_completed
});

export const normalizeSubtasks = (
  subtasks: SubTask[] | ToDoPlayerSubTask[]
): ToDoPlayerSubTask[] =>
  subtasks.map((subtask) => ({
    id:
      'id' in subtask && typeof subtask.id === 'string' && subtask.id.length > 0
        ? subtask.id
        : uuid(),
    name: subtask.name.trim(),
    is_completed: Boolean(subtask.is_completed)
  }));

export function resolveTaskRemainingDuration(
  taskDuration: number,
  focusedDuration = 0
): { remainingDuration: number; focusedDuration: number } {
  let focused = focusedDuration;
  let remaining = taskDuration - focused;
  if (remaining <= 0) {
    remaining = taskDuration;
    focused = 0;
  }
  return { remainingDuration: remaining, focusedDuration: focused };
}

export const getTodoPlayerTabPanelClassName = (
  tabIndex: number,
  selectedTabIndex: number
): string =>
  cn(
    'todo-player-tab-panel',
    tabIndex === selectedTabIndex ? 'flex' : 'hidden'
  );

/** List play: start a new task or resume the selected task (keeps paused counter when resuming). */
export function getPlayerDataAfterListPlay(
  prev: PlayerDataType,
  remainingDuration: number,
  isSameSelectedTask: boolean
): PlayerDataType {
  const shouldInitializeCounter = !prev.isPlayingStarted && prev.counter === 0;

  let counter = remainingDuration;
  if (isSameSelectedTask && prev.isPlayingStarted && !shouldInitializeCounter) {
    counter = prev.counter > 0 ? prev.counter : remainingDuration;
  }

  return {
    ...prev,
    counter,
    isPlaying: true,
    isPlayingStarted: true
  };
}

export function getEffectiveQuickAddDurationSeconds(
  durationSeconds: number | undefined,
  fallbackSeconds: number
): number {
  return durationSeconds !== undefined && durationSeconds > 0
    ? durationSeconds
    : fallbackSeconds;
}

export function buildFormattedQuickAddPlayerTask(params: {
  trimmedTitle: string;
  effectiveDurationSeconds: number;
}): ToDoPlayerTask {
  const base = DEFAULT_TO_DO_PLAYER_TASK();
  base.title = params.trimmedTitle;
  base.duration = params.effectiveDurationSeconds;
  const [formattedNew] = formatTasksForPlayer([base]);
  return formattedNew;
}

export function applyQuickAddCreatedTask(params: {
  dispatch: AppDispatch;
  formattedNew: ToDoPlayerTask;
  created: ToDoPlayerTask;
  playerTasks: ToDoPlayerTask[] | undefined;
  todoListTasks: ToDoTask[] | undefined;
}): void {
  const { dispatch, formattedNew, created, playerTasks, todoListTasks } =
    params;
  const newTaskForPlayer: ToDoPlayerTask = {
    ...formattedNew,
    ...created,
    focusedDuration: 0,
    isPlaying: false
  };
  const mergedPlayerTasks = formatToDoPlayerTaskTimeConstraints([
    ...(playerTasks ?? []),
    newTaskForPlayer
  ]);
  dispatch(updatePlayerTasks(mergedPlayerTasks));
  dispatch(updateTasks([created, ...(todoListTasks ?? [])]));
  dispatch(updatePlayerIsReadyToSendUpdates(true));
  capturePostHogEvent(POST_HOG_EVENT.TODO_PLAYER_NEW_TASK_CREATED, {
    taskId: created.id,
    taskTitle: created.title
  });
}

export const getTodoPlayerRemainingSeconds = ({
  totalDuration,
  focusModeEndTime,
  isPausedFromDesktop = false,
  focusModePauseStartedAtMs = null,
  focusModeAccumulatedPauseOffsetMs = 0
}: RemainingSecondsParams) => {
  if (!focusModeEndTime) {
    return totalDuration ?? null;
  }

  const inProgressPauseOffsetMs =
    isPausedFromDesktop && focusModePauseStartedAtMs !== null
      ? Date.now() - focusModePauseStartedAtMs
      : 0;
  const effectivePauseOffsetMs =
    focusModeAccumulatedPauseOffsetMs + inProgressPauseOffsetMs;
  const endTime = moment
    .utc(focusModeEndTime)
    .add(effectivePauseOffsetMs, 'milliseconds');
  const now = moment.utc();
  const remainingSeconds = endTime.diff(now, 'seconds');

  return Math.max(remainingSeconds, TO_DO_PLAYER.MINIMUM_REMAINING_SECONDS);
};

export const durationSecondsToEditingFields = (
  durationSeconds: number
): { hours: string; minutes: string } => {
  const { hours, minutes } = splitPlayerDurationSeconds(durationSeconds);
  return { hours: hours.toString(), minutes: minutes.toString() };
};

export const TOP_COMPONENT_SCORE_MAX = RANGE_SLIDER_STEPS.MAX;

export const topComponentScoreBarWidth = (
  value?: number,
  max = TOP_COMPONENT_SCORE_MAX
): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0%';
  }
  const clamped = clamp(value, 0, max);
  return `${ratioToPercent(clamped, max)}%`;
};

export const formatTopComponentScore = (value?: number): string | undefined =>
  formatToFixedDecimals(value, 1);

export const formatTopScoreTotal = (value?: number): string | undefined =>
  formatToFixedDecimals(value, 1);

export const formatTopScoreTotalLabel = (
  value?: number,
  fallback = '-'
): string => formatTopScoreTotal(value) ?? fallback;
